import { MoreHorizontal } from '@griply/assets';
import { CalendarEventRef } from '@griply/types';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Colors, IconButton, Sizes } from 'shared/components/ui/icon-button';
import {
  ResizableTextArea,
  Variants as ResizableTextAreaVariants,
} from 'shared/components/ui/resizable-text-area';
// the hook file, not the contexts barrel — the barrel reaches the provider, which renders this form
import { useExternalEventMenu } from 'shared/contexts/event-form/use-external-event-menu';
import { useOpenMenu } from 'shared/hooks/use-open-menu';

import * as Styled from './event-form.style';

export type EventFormHeaderProps = {
  value: string;
  onChange?: (title: string) => void;
  disabled?: boolean;
  titleRef?: React.RefObject<HTMLTextAreaElement>;
  // the actions menu belongs to a saved event — a create popup has nothing to act on yet
  eventTarget?: CalendarEventRef;
  isRecurring?: boolean;
  isOrganizer?: boolean;
  onDuplicate?: () => void;
  // the popup cannot stay open over an event the menu just deleted or copied away from
  onAfterAction?: () => void;
};

export const EventFormHeader: React.FC<EventFormHeaderProps> = ({
  value,
  onChange,
  disabled,
  titleRef,
  eventTarget,
  isRecurring,
  isOrganizer,
  onDuplicate,
  onAfterAction,
}) => {
  const { t } = useTranslation();
  const menuTriggerRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();
  const ExternalEventMenu = useExternalEventMenu();

  const isReadOnly = !!disabled || !onChange;

  return (
    <Styled.Header>
      <Styled.TitleColumn>
        {isReadOnly && <Styled.Title>{value}</Styled.Title>}
        {!isReadOnly && !!onChange && (
          <ResizableTextArea
            ref={titleRef}
            value={value}
            onChange={onChange}
            variant={ResizableTextAreaVariants.Subtitle2}
            placeholder={t('forms.event.fields.title.placeholder')}
            fullWidth
          />
        )}
      </Styled.TitleColumn>

      {!!eventTarget && (
        <Styled.HeaderMenuAnchor ref={menuTriggerRef}>
          <IconButton
            icon={MoreHorizontal}
            size={Sizes.Small}
            color={Colors.Default}
            onClick={openMenu}
            type="button"
            aria-label={t('context-menus.external-event.labels.more')}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          />
          {menuOpen && (
            // no Edit: this menu only opens from inside the event it would navigate to
            <ExternalEventMenu
              eventTarget={eventTarget}
              isRecurring={!!isRecurring}
              isOrganizer={isOrganizer}
              referenceElement={menuTriggerRef}
              position="bottom-end"
              onDuplicate={onDuplicate}
              onClose={closeMenu}
              onAfterAction={onAfterAction}
            />
          )}
        </Styled.HeaderMenuAnchor>
      )}
    </Styled.Header>
  );
};
